import { Center, Loader, MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { lazy, StrictMode, Suspense, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import './app.css';

import { AdminLayout } from './routes/AdminLayout.js';
import { InvitePage } from './routes/InvitePage.js';
import { LandingPage } from './routes/LandingPage.js';
import { PrivacyPage, SupportPage } from './routes/LegalPage.js';
import { LoginPage } from './routes/LoginPage.js';
import { ProvisionPage } from './routes/ProvisionPage.js';
import { AuthGate } from './auth/AuthGate.js';
import { PortalLayout } from './portal/PortalLayout.js';
import { SsoCallback } from './auth/SsoCallback.js';
import { theme } from './theme.js';

/*
 * Every console and portal page loads on demand. The landing page, login and
 * the two layouts stay eager: they are what a first visit needs, and splitting
 * them would only add a round trip before anything paints.
 */
const LeadsPage = lazy(() => import('./routes/LeadsPage.js').then((x) => ({ default: x.LeadsPage })));
const OpportunitiesPage = lazy(() =>
  import('./routes/OpportunitiesPage.js').then((x) => ({ default: x.OpportunitiesPage })),
);
const AuditPage = lazy(() => import('./routes/AuditPage.js').then((m) => ({ default: m.AuditPage })));
const DealerDetailPage = lazy(() => import('./routes/DealerDetailPage.js').then((m) => ({ default: m.DealerDetailPage })));
const AccountPage = lazy(() => import('./routes/AccountPage.js').then((m) => ({ default: m.AccountPage })));
const ActivityPage = lazy(() => import('./routes/ActivityPage.js').then((m) => ({ default: m.ActivityPage })));
const DealersPage = lazy(() => import('./routes/DealersPage.js').then((m) => ({ default: m.DealersPage })));
const DocsPage = lazy(() => import('./routes/DocsPage.js').then((m) => ({ default: m.DocsPage })));
const EmailPage = lazy(() => import('./routes/EmailPage.js').then((m) => ({ default: m.EmailPage })));
const EmailsPage = lazy(() => import('./routes/EmailsPage.js').then((m) => ({ default: m.EmailsPage })));
const EmailLabPage = lazy(() => import('./routes/EmailLabPage.js').then((m) => ({ default: m.EmailLabPage })));
const InvitationsPage = lazy(() => import('./routes/InvitationsPage.js').then((m) => ({ default: m.InvitationsPage })));
const RolesPage = lazy(() => import('./routes/RolesPage.js').then((m) => ({ default: m.RolesPage })));
const KeysPage = lazy(() => import('./routes/KeysPage.js').then((m) => ({ default: m.KeysPage })));
const PlaygroundPage = lazy(() => import('./routes/PlaygroundPage.js').then((m) => ({ default: m.PlaygroundPage })));
const OverviewPage = lazy(() => import('./routes/OverviewPage.js').then((m) => ({ default: m.OverviewPage })));
const SettingsPage = lazy(() => import('./routes/SettingsPage.js').then((m) => ({ default: m.SettingsPage })));
const UsersPage = lazy(() => import('./routes/UsersPage.js').then((m) => ({ default: m.UsersPage })));
const PortalActivity = lazy(() => import('./portal/PortalPages.js').then((m) => ({ default: m.PortalActivity })));
const PortalCalls = lazy(() => import('./portal/PortalPages.js').then((m) => ({ default: m.PortalCalls })));
const PortalHome = lazy(() => import('./portal/PortalPages.js').then((m) => ({ default: m.PortalHome })));
const PortalLeads = lazy(() => import('./portal/PortalPages.js').then((m) => ({ default: m.PortalLeads })));
const PortalOpportunities = lazy(() =>
  import('./portal/PortalPages.js').then((m) => ({ default: m.PortalOpportunities })),
);
const PortalSettings = lazy(() => import('./portal/PortalPages.js').then((m) => ({ default: m.PortalSettings })));

/** One fallback for every lazy route, so a chunk loading never shows a blank page. */
function Lazy({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <Center mih="40vh">
          <Loader />
        </Center>
      }
    >
      {children}
    </Suspense>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session ended; retrying only delays the redirect.
      retry: (count, error) => ((error as { status?: number }).status === 401 ? false : count < 2),
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

const router = createBrowserRouter([
  // Public marketing page. The console lives under /app so the apex is free
  // for anyone who is not signed in.
  { path: '/', element: <LandingPage /> },
  { path: '/login', element: <LoginPage /> },
  // Social providers return here. Without the route they land on a 404
  // and the sign-in dies at the final step.
  { path: '/login/sso-callback', element: <SsoCallback /> },
  // Public: dealers land here from their CRM's portal.
  { path: '/provision', element: <ProvisionPage /> },
  // Public by necessity: an invitee has no account yet.
  { path: '/invite/:token', element: <InvitePage /> },
  // Both are required fields on the CDK Marketplace listing and a reviewer
  // clicks them, so they must resolve without a session.
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/support', element: <SupportPage /> },
  /**
   * The dealership area. A separate tree from /app rather than the console
   * with things hidden: a dealership should never see a nav item, a page
   * title or an error that implies other dealerships exist.
   */
  {
    path: '/portal',
    element: <PortalLayout />,
    children: [
      { index: true, element: <Lazy><PortalHome /></Lazy> },
      { path: 'calls', element: <Lazy><PortalCalls /></Lazy> },
      { path: 'leads', element: <Lazy><PortalLeads /></Lazy> },
      { path: 'opportunities', element: <Lazy><PortalOpportunities /></Lazy> },
      { path: 'settings', element: <Lazy><PortalSettings /></Lazy> },
      { path: 'activity', element: <Lazy><PortalActivity /></Lazy> },
    ],
  },
  {
    path: '/app',
    element: <AdminLayout />,
    children: [
      { index: true, element: <Lazy><OverviewPage /></Lazy> },
      { path: 'dealers', element: <Lazy><DealersPage /></Lazy> },
      { path: 'dealers/:id', element: <Lazy><DealerDetailPage /></Lazy> },
      { path: 'leads', element: <Lazy><LeadsPage /></Lazy> },
      { path: 'opportunities', element: <Lazy><OpportunitiesPage /></Lazy> },
      { path: 'audit', element: <Lazy><AuditPage /></Lazy> },
      { path: 'users', element: <Lazy><UsersPage /></Lazy> },
      { path: 'invitations', element: <Lazy><InvitationsPage /></Lazy> },
      { path: 'roles', element: <Lazy><RolesPage /></Lazy> },
      { path: 'keys', element: <Lazy><KeysPage /></Lazy> },
      { path: 'playground', element: <Lazy><PlaygroundPage /></Lazy> },
      { path: 'docs', element: <Lazy><DocsPage /></Lazy> },
      { path: 'activity', element: <Lazy><ActivityPage /></Lazy> },
      { path: 'account', element: <Lazy><AccountPage /></Lazy> },
      { path: 'email', element: <Lazy><EmailPage /></Lazy> },
      { path: 'email-lab', element: <Lazy><EmailLabPage /></Lazy> },
      { path: 'emails', element: <Lazy><EmailsPage /></Lazy> },
      { path: 'settings', element: <Lazy><SettingsPage /></Lazy> },
    ],
  },
]);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="auto">
      <Notifications position="top-right" />
      {/* QueryClientProvider must be OUTSIDE ModalsProvider.
          modals.open() renders its content in the ModalsProvider's tree, not
          where the call was made, so anything above it is invisible to that
          content. With the order reversed, a useQuery inside a modal threw
          "No QueryClient set" and the modal silently never appeared. */}
      <QueryClientProvider client={queryClient}>
        <ModalsProvider>
          {/* AuthGate asks the API which mechanism is in use before deciding
              whether to mount Clerk at all. */}
          <AuthGate>
            <RouterProvider router={router} />
          </AuthGate>
        </ModalsProvider>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
